import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PlanGenerationScreen from './PlanGenerationScreen';
import { UserService } from '../../services/ApiService';
import { additionalContext } from '../../data/context';

const OnboardingScreen = ({ navigation, onOnboardingComplete }) => {
  const insets = useSafeAreaInsets();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: 'male',
    goal: 'lose fat',
    customGoal: '',
    height: '',
    weight: '',
    trainingDays: '3',
    numberOfMeals: '3',
    dietaryPreference: 'omnivore',
    allergies: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPlanGeneration, setShowPlanGeneration] = useState(false);
  const [planGenerationData, setPlanGenerationData] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);

  const goals = [
    { label: 'Lose Fat', value: 'lose fat' },
    { label: 'Gain Muscle', value: 'gain muscle' },
    { label: 'Maintain Weight', value: 'maintain' },
    { label: 'Improve Fitness', value: 'improve fitness' },
    { label: 'General Health', value: 'general health' },
    { label: 'Others', value: 'others' },
  ];

  const numberOfMealsOptions = [
    { label: '2 Meals', value: '2' },
    { label: '3 Meals', value: '3' },
    { label: '4 Meals', value: '4' },
    { label: '5 Meals', value: '5' },
    { label: '6 Meals', value: '6' },
  ];

  const dietaryPreferences = [
    { label: 'Omnivore', value: 'omnivore' },
    { label: 'Vegetarian', value: 'vegetarian' },
    { label: 'Vegan', value: 'vegan' },
    { label: 'Keto', value: 'keto' },
    { label: 'Paleo', value: 'paleo' },
    { label: 'Mediterranean', value: 'mediterranean' },
    { label: 'Flexitarian', value: 'flexitarian' },
  ];

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = ['name', 'age', 'height', 'weight'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        Alert.alert('Error', `Please fill in ${field}`);
        return;
      }
    }

    // Validate custom goal if "others" is selected
    if (formData.goal === 'others' && !formData.customGoal.trim()) {
      Alert.alert('Error', 'Please specify your custom goal');
      return;
    }

    setLoading(true);
    try {
      const allergiesArray = formData.allergies
        ? formData.allergies.split(',').map(a => a.trim()).filter(a => a)
        : [];

      const onboardingData = {
        age: parseInt(formData.age),
        gender: formData.gender,
        goal: formData.goal,
        customGoal: formData.goal === 'others' ? formData.customGoal.trim() : null,
        height: parseInt(formData.height),
        weight: parseFloat(formData.weight),
        trainingDays: parseInt(formData.trainingDays),
        numberOfMeals: parseInt(formData.numberOfMeals),
        dietaryPreference: formData.dietaryPreference,
        allergies: allergiesArray,
        additionalContext: additionalContext,
        isTentativePlan: true
      };

      // Store data for later use in TentativePlanScreen
      setSubmittedData(onboardingData);

      const result = await UserService.completeOnboarding(onboardingData);
      console.log('Onboarding completed successfully:', result);

      // Check if plan generation is in progress
      if (result.data.planGenerationInProgress && result.data.jobId) {
        console.log('Plan generation started, showing loading screen...');
        setPlanGenerationData({
          jobId: result.data.jobId,
          planType: result.data.planType
        });
        setShowPlanGeneration(true);
      } else {
        // No plan generation needed
        const message = result.data.canGeneratePlan
          ? 'Welcome! Your profile has been updated. You can generate your plan from the dashboard.'
          : 'Welcome! Your profile has been updated.';

        Alert.alert('Success', message, [
          {
            text: 'Go to Dashboard', onPress: async () => {
              console.log('Calling onOnboardingComplete callback...');
              if (onOnboardingComplete) {
                await onOnboardingComplete();
                console.log('Onboarding complete callback executed');
              }
              // Navigate to Main screen (which contains the Dashboard tab)
              navigation.navigate('Main');
            }
          }
        ]);
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      Alert.alert('Error', 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanGenerationComplete = async (result) => {
    console.log('Plan generation completed in OnboardingScreen:', result);
    setShowPlanGeneration(false);

    // Navigate to Tentative Plan screen for review
    console.log('Navigating to TentativePlan screen...');
    try {
      navigation.navigate('TentativePlan', {
        planData: result,
        planType: planGenerationData.planType,
        jobId: planGenerationData.jobId,
        onboardingData: submittedData
      });
      console.log('Navigation.navigate(TentativePlan) called successfully');
    } catch (navError) {
      console.error('Navigation error:', navError);
      Alert.alert('Navigation Error', 'Could not navigate to plan review.');
    }
  };

  const handlePlanGenerationError = async (error) => {
    console.error('Plan generation failed in OnboardingScreen:', error);
    setShowPlanGeneration(false);

    // Call the onboarding complete callback first
    console.log('Calling onOnboardingComplete callback...');
    if (onOnboardingComplete) {
      await onOnboardingComplete();
      console.log('Onboarding complete callback executed');
    }

    // Navigate to Main screen (which contains the Dashboard tab)
    console.log('Navigating to Main screen after error...');
    try {
      navigation.navigate('Main');
      console.log('Navigation.navigate(Main) called successfully');
    } catch (navError) {
      console.error('Navigation error:', navError);
      // Try alternative navigation method
      try {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Main' }],
        });
        console.log('Navigation.reset to Main called successfully');
      } catch (resetError) {
        console.error('Navigation reset error:', resetError);
      }
    }

    // Show error alert after navigation
    setTimeout(() => {
      Alert.alert(
        'Plan Generation Failed',
        'We encountered an issue generating your plan. Don\'t worry, you can try generating it again from the dashboard.'
      );
    }, 1000);
  };

  // Show plan generation screen if plan is being generated
  if (showPlanGeneration && planGenerationData) {
    return (
      <PlanGenerationScreen
        jobId={planGenerationData.jobId}
        planType={planGenerationData.planType}
        onComplete={handlePlanGenerationComplete}
        onError={handlePlanGenerationError}
      />
    );
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { paddingTop: insets.top }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView style={styles.scrollView}>
        <View style={styles.header}>
          <Text style={styles.title}>Complete Your Profile</Text>
          <Text style={styles.subtitle}>
            Help us create the perfect fitness plan for you
          </Text>
        </View>

        <View style={styles.form}>
          <Text style={styles.label}>Name</Text>
          <TextInput
            style={styles.input}
            placeholderTextColor="#999"
            value={formData.name}
            onChangeText={(text) => setFormData({ ...formData, name: text })}
            placeholder="Enter your name"
          />

          <Text style={styles.label}>Age</Text>
          <TextInput
            style={styles.input}
            placeholderTextColor="#999"
            value={formData.age}
            onChangeText={(text) => setFormData({ ...formData, age: text })}
            placeholder="Enter your age"
            keyboardType="numeric"
          />

          <Text style={styles.label}>Gender</Text>
          <View style={styles.pickerContainer}>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              style={webSelectStyles}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </View>

          <Text style={styles.label}>Goal</Text>
          <View style={styles.goalContainer}>
            {goals.map((goal) => (
              <TouchableOpacity
                key={goal.value}
                style={[
                  styles.goalButton,
                  formData.goal === goal.value && styles.goalButtonSelected
                ]}
                onPress={() => setFormData({ ...formData, goal: goal.value, customGoal: goal.value === 'others' ? formData.customGoal : '' })}
              >
                <Text style={[
                  styles.goalButtonText,
                  formData.goal === goal.value && styles.goalButtonTextSelected
                ]}>
                  {goal.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Custom Goal Input - Only show when "Others" is selected */}
          {formData.goal === 'others' && (
            <View style={styles.customGoalContainer}>
              <Text style={styles.label}>Please specify your goal</Text>
              <TextInput
                style={styles.input}
                placeholderTextColor="#999"
                value={formData.customGoal}
                onChangeText={(text) => setFormData({ ...formData, customGoal: text })}
                placeholder="Enter your specific fitness goal"
                multiline
                numberOfLines={2}
              />
            </View>
          )}

          <Text style={styles.label}>Height (cm)</Text>
          <TextInput
            style={styles.input}
            placeholderTextColor="#999"
            value={formData.height}
            onChangeText={(text) => setFormData({ ...formData, height: text })}
            placeholder="Enter your height"
            keyboardType="numeric"
          />

          <Text style={styles.label}>Weight (kg)</Text>
          <TextInput
            style={styles.input}
            placeholderTextColor="#999"
            value={formData.weight}
            onChangeText={(text) => setFormData({ ...formData, weight: text })}
            placeholder="Enter your weight"
            keyboardType="numeric"
          />

          <Text style={styles.label}>Training Days per Week</Text>
          <View style={styles.pickerContainer}>
            <select
              value={formData.trainingDays}
              onChange={(e) => setFormData({ ...formData, trainingDays: e.target.value })}
              style={webSelectStyles}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((days) => (
                <option key={days} value={days.toString()}>
                  {days} days
                </option>
              ))}
            </select>
          </View>

          <Text style={styles.label}>Number of Meals per Day</Text>
          <View style={styles.pickerContainer}>
            <select
              value={formData.numberOfMeals}
              onChange={(e) => setFormData({ ...formData, numberOfMeals: e.target.value })}
              style={webSelectStyles}
            >
              {numberOfMealsOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </View>

          <Text style={styles.label}>Dietary Preference</Text>
          <View style={styles.pickerContainer}>
            <select
              value={formData.dietaryPreference}
              onChange={(e) => setFormData({ ...formData, dietaryPreference: e.target.value })}
              style={webSelectStyles}
            >
              {dietaryPreferences.map((pref) => (
                <option key={pref.value} value={pref.value}>
                  {pref.label}
                </option>
              ))}
            </select>
          </View>

          <Text style={styles.label}>Allergies (comma separated)</Text>
          <TextInput
            style={styles.input}
            placeholderTextColor="#999"
            value={formData.allergies}
            onChangeText={(text) => setFormData({ ...formData, allergies: text })}
            placeholder="e.g., peanuts, dairy, gluten"
          />

          <TouchableOpacity
            style={[styles.button, loading && styles.buttonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            <Text style={styles.buttonText}>
              {loading ? 'Saving...' : 'Complete Setup'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  scrollView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    padding: 20,
    paddingTop: 40,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#3B82F6',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6D759E',
    textAlign: 'center',
  },
  form: {
    padding: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 8,
    marginTop: 16,
  },
  input: {
    backgroundColor: '#161C2D',
    color: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  pickerContainer: {
    backgroundColor: '#161C2D',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  picker: {
    height: 50,
  },
  button: {
    backgroundColor: '#3B82F6',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 30,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  goalContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  goalButton: {
    backgroundColor: '#161C2D',
    borderWidth: 2,
    borderColor: '#e1e5e9',
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
    minWidth: '45%',
    alignItems: 'center',
  },
  goalButtonSelected: {
    borderColor: '#3B82F6',
    backgroundColor: '#0B0E14',
  },
  goalButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  goalButtonTextSelected: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  customGoalContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
});

const webSelectStyles = {
  fontSize: 16,
  paddingVertical: 12,
  paddingHorizontal: 10,
  borderWidth: 1,
  borderColor: '#e1e5e9',
  borderRadius: 8,
  color: '#FFFFFF',
  backgroundColor: '#161C2D',
  width: '100%',
  height: 50,
};

export default OnboardingScreen;
